import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  FC,
  type RefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import meKeys from '@/api/queries/me/keyFactory';
import useGetMe from '@/api/queries/me/useGetMe';

import { type ChatAgentBinding } from './types';
import {
  type PageContext,
  type UseChatStreamReturn,
  useChatStream,
} from './useChatStream';

export type SendChatMessageOptions = {
  prompt: string;
  /** Start a fresh conversation instead of the active one. Defaults to false. */
  forceNewConversation?: boolean;
  /** Open the chat panel. Defaults to true. */
  openPanel?: boolean;
};

type ChatAgentContextType = {
  hasChatAgentAccess: boolean;
  show: boolean;
  showChatAgent: () => void;
  hideChatAgent: () => void;
  /** Submits a message to Agent Dave and (by default) opens the panel. */
  sendMessage: (options: SendChatMessageOptions) => void;
  /**
   * Stops the in-flight response and sends the message immediately as the next
   * turn, rather than queueing it behind the current one.
   */
  interruptAndSend: (prompt: string) => void;
  /** The chat stream state and controls, consumed by the ChatAgent panel. */
  chat: UseChatStreamReturn;
  /** Ref for the chat input textarea, so focus can be restored after a reply. */
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** The page binding, if a page has bound the panel. */
  binding: ChatAgentBinding | null;
  /** Opens the panel unless `open: false`. */
  openChatAgentWithBinding: (
    binding: ChatAgentBinding,
    options?: { open?: boolean }
  ) => void;
  /** Unbind the panel (call when the bound page unmounts). */
  clearBinding: () => void;
  /**
   * Bumped each time Agent Dave completes a mutating tool call. Key a surface
   * on it to remount it onto the refetched data — the way to follow a write
   * the surface didn't make itself, without a form having to diff its own
   * value against a server value its own saves feed.
   */
  agentWriteRevision: number;
};

const defaultChat: UseChatStreamReturn = {
  messages: [],
  isStreaming: false,
  error: null,
  isDisabled: false,
  status: null,
  conversationId: null,
  isLoading: false,
  streamingConversationIds: new Set(),
  queuedMessages: [],
  sendMessage: async () => {},
  stopStreaming: () => {},
  interruptAndSend: async () => {},
  cancelQueuedMessage: () => {},
  clearMessages: () => {},
  switchConversation: () => {},
};

// Context value used for users without chat agent access. No hooks run, so
// nothing here does anything — the ChatAgent panel is not rendered for them.
const noAccessContextValue: ChatAgentContextType = {
  hasChatAgentAccess: false,
  show: false,
  showChatAgent: () => {},
  hideChatAgent: () => {},
  sendMessage: () => {},
  interruptAndSend: () => {},
  chat: defaultChat,
  inputRef: { current: null },
  binding: null,
  openChatAgentWithBinding: () => {},
  clearBinding: () => {},
  agentWriteRevision: 0,
};

const ChatAgentContext =
  createContext<ChatAgentContextType>(noAccessContextValue);

export const useChatAgent = () => useContext(ChatAgentContext);

/**
 * Users without `hasChatAgentAccess` get an inert context, so none of the chat
 * machinery (`useChatStream` etc.) is initialised for them.
 */
const ChatAgentContextProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const { hasChatAgentAccess } = useGetMe();

  if (!hasChatAgentAccess) {
    return (
      <ChatAgentContext.Provider value={noAccessContextValue}>
        {children}
      </ChatAgentContext.Provider>
    );
  }

  return <ActiveChatAgentProvider>{children}</ActiveChatAgentProvider>;
};

const ActiveChatAgentProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  const queryClient = useQueryClient();
  const location = useLocation();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [isShown, setIsShown] = useState(false);
  const showChatAgent = useCallback(() => setIsShown(true), []);
  const hideChatAgent = useCallback(() => setIsShown(false), []);

  const [binding, setBinding] = useState<ChatAgentBinding | null>(null);

  // Mirrors for reading the latest state inside the stable callbacks below.
  const isShownRef = useRef(isShown);
  isShownRef.current = isShown;
  const bindingRef = useRef(binding);
  bindingRef.current = binding;

  // The panel state before a bound page first toggled it, restored when the
  // binding clears so leaving the page puts the panel back how it was found.
  const preBindingIsShownRef = useRef<boolean | null>(null);

  const openChatAgentWithBinding = useCallback(
    (nextBinding: ChatAgentBinding, options?: { open?: boolean }) => {
      if (bindingRef.current === null) {
        preBindingIsShownRef.current = isShownRef.current;
      }
      setBinding(nextBinding);
      if (options?.open !== false) setIsShown(true);
    },
    []
  );
  const clearBinding = useCallback(() => {
    setBinding(null);
    if (preBindingIsShownRef.current !== null) {
      setIsShown(preBindingIsShownRef.current);
      preBindingIsShownRef.current = null;
    }
  }, []);

  const [agentWriteRevision, setAgentWriteRevision] = useState(0);

  const chat = useChatStream({
    onMutationComplete: () => {
      // Bump only once the refetches have settled, so a surface keyed on the
      // revision remounts onto the new data rather than the pre-write cache.
      void queryClient
        .invalidateQueries()
        .then(() => setAgentWriteRevision((revision) => revision + 1));
    },
    onNewChatConfirmation: () => {
      void queryClient.invalidateQueries({
        queryKey: meKeys.chatAgentConversations(),
      });
      inputRef.current?.focus();
    },
    onStreamComplete: () => {
      void queryClient.invalidateQueries({
        queryKey: meKeys.chatAgentConversations(),
      });
      inputRef.current?.focus();
    },
  });

  const { switchConversation, conversationId: activeConversationId } = chat;

  // switchConversation changes identity on every stream update, so a ref guards
  // it to once per binding, leaving the user free to switch manually. Skipping an
  // already-active target stops a rebind after navigation reloading it.
  const lastHandledBindingRef = useRef<ChatAgentBinding | null>(null);
  useEffect(() => {
    if (binding === lastHandledBindingRef.current) return;
    lastHandledBindingRef.current = binding;
    if (!binding) return;
    const target = binding.conversationId ?? null;
    if (target !== activeConversationId) switchConversation(target);
  }, [binding, switchConversation, activeConversationId]);

  // Report the active conversation to the bound page so it can react to what
  // the agent does (e.g. detect an entity the agent created and linked).
  const onActiveConversationChange = binding?.onActiveConversationChange;
  useEffect(() => {
    onActiveConversationChange?.(activeConversationId);
  }, [onActiveConversationChange, activeConversationId]);

  // Escape stops an in-flight response, or closes the panel when nothing is
  // streaming, so an interrupt never also dismisses the panel.
  const { isStreaming, stopStreaming } = chat;
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsShown((prev) => !prev);
      }

      if (e.key === 'Escape' && isShown) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (isStreaming) {
          stopStreaming();
        } else {
          setIsShown(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isShown, isStreaming, stopStreaming]);

  // `useRouteMatch`/`useParams` aren't used because this sits above the route
  // definitions — `useLocation` gives the correct path.
  const buildPageContext = useCallback(
    (): PageContext => ({
      pathname: location.pathname,
      searchParams: Object.fromEntries(new URLSearchParams(location.search)),
      mountedQueryKeys: queryClient
        .getQueryCache()
        .getAll()
        .filter((q) => q.state.status === 'success')
        .map((q) => q.queryKey as unknown[]),
      ...bindingRef.current?.pageContext,
    }),
    [location.pathname, location.search, queryClient]
  );

  const { sendMessage: streamSendMessage, interruptAndSend: streamInterrupt } =
    chat;
  const sendMessage = useCallback(
    (options: SendChatMessageOptions) => {
      if (options.openPanel !== false) {
        setIsShown(true);
      }
      void streamSendMessage(options.prompt, buildPageContext(), {
        forceNewConversation: options.forceNewConversation,
      });
    },
    [buildPageContext, streamSendMessage]
  );

  const interruptAndSend = useCallback(
    (prompt: string) => {
      void streamInterrupt(prompt, buildPageContext());
    },
    [buildPageContext, streamInterrupt]
  );

  const contextValue = useMemo<ChatAgentContextType>(
    () => ({
      hasChatAgentAccess: true,
      show: isShown,
      showChatAgent,
      hideChatAgent,
      sendMessage,
      interruptAndSend,
      chat,
      inputRef,
      binding,
      openChatAgentWithBinding,
      clearBinding,
      agentWriteRevision,
    }),
    [
      isShown,
      showChatAgent,
      hideChatAgent,
      sendMessage,
      interruptAndSend,
      chat,
      binding,
      openChatAgentWithBinding,
      clearBinding,
      agentWriteRevision,
    ]
  );

  return (
    <ChatAgentContext.Provider value={contextValue}>
      {children}
    </ChatAgentContext.Provider>
  );
};

export default ChatAgentContextProvider;
